import { useCallback, useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { ApiError, storeToken } from '../../lib/api';
import { useMe } from '../../hooks/useMe';
import { useApplyTheme } from '../../hooks/useTheme';
import { useRealtime } from '../../lib/realtime';
import { RingProvider } from '../../lib/ring';
import { AccountFilterProvider } from '../../lib/accountFilter';
import { NotificationProvider } from '../../lib/notifications';
import { ErrorState, Spinner } from '../ui/Feedback';
import { AppShell } from './AppShell';
import { AppProblem } from './ErrorBoundary';

function Splash() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-white shadow-primary">
        <Send className="h-7 w-7" />
      </div>
      <Spinner label="Loading MailPilot" />
    </div>
  );
}

export function RequireAuth() {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const s = me.data?.settings;
  useApplyTheme(s?.theme, s?.fontScale, s ? { accent: s.accent, sidebarStyle: s.sidebarStyle, density: s.density } : undefined);
  const onRevoked = useCallback(() => {
    qc.removeQueries({ queryKey: ['me'] });
    void storeToken(null);
    navigate('/login?revoked=1', { replace: true });
  }, [qc, navigate]);
  // Details without a workspace come from an old offline copy or an old server: re-check once, right away.
  const rechecked = useRef(false);
  useEffect(() => {
    if (me.data && !me.data.workspace && !rechecked.current) {
      rechecked.current = true;
      void me.refetch();
    }
  }, [me.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useRealtime(!!me.data?.workspace, me.data?.sessionId, me.data?.workspace?.id, onRevoked);

  // A server older than this app answers without workspace information: say so instead of crashing.
  if (me.data && !me.data.workspace) {
    // Possibly an offline copy saved by an older version: wait for the fresh answer before judging.
    if (me.isFetching || !rechecked.current) return <Splash />;
    return (
      <AppProblem
        title="The MailPilot server needs updating"
        message="This app is newer than the server it's connected to, so it can't open your mail yet."
        hint="Restart the MailPilot server with the latest version, then press Reload."
      />
    );
  }
  if (me.data) {
    // First-time setup: everything except returning from a provider sign-in or approving a device.
    const onboarding = !me.data.settings.onboardedAt;
    const exempt = /^\/(welcome|settings\/accounts|link-device)(\/|$)/.test(location.pathname);
    if (onboarding && !exempt) return <Navigate to="/welcome" replace />;
    const page = location.pathname === '/welcome' ? <Outlet /> : (
      <AppShell>
        <Outlet />
      </AppShell>
    );
    return (
      // Keyed by workspace: switching resets the account filter and every per-workspace provider.
      <AccountFilterProvider key={me.data.workspace.id} workspaceId={me.data.workspace.id}>
        <RingProvider>
          <NotificationProvider>{page}</NotificationProvider>
        </RingProvider>
      </AccountFilterProvider>
    );
  }
  if (me.isPending) return <Splash />;
  if (me.error instanceof ApiError && me.error.status === 401) {
    const next = location.pathname + location.search;
    return <Navigate to={`/login${next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />;
  }
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-md rounded-card bg-surface shadow-card">
        <ErrorState error={me.error} title="Can't reach MailPilot" onRetry={() => void me.refetch()} />
      </div>
    </div>
  );
}

export function PublicOnly() {
  const me = useMe();
  useApplyTheme(undefined, undefined);
  if (me.data) return <Navigate to="/inbox" replace />;
  return <Outlet />;
}
